import { useState, useEffect, useMemo } from 'react';
import { Integration, InviteThirdPartyTeamsIneligibilityReasons } from '../../types';
import { isMobile } from '../../utils';
import { useIntl } from 'react-intl-next';
import { useExperimentalFeatures } from '../experimentalFeatures';
import { useInvitePeopleContext } from '../../components/InvitePeople';
import { useAnalytics } from '../../analytics';
import { ExperimentsAnalytics } from '../ExperimentsAnalytics';

export interface InviteThirdPartyTeamsEligibilityProps {
	isThirdPartyStateLoading: boolean;
	integrations: Set<Integration>;
	enabledIntegrations: Set<Integration>;
}

export interface InviteThirdPartyTeamsState {
	isLoading: boolean;
	isEnabled: boolean;
	onSlackConnected: boolean;
	setOnSlackConnected: (value: boolean) => void;
}

export const useInviteThirdPartyTeamsEligibility = ({
	isThirdPartyStateLoading,
	integrations,
	enabledIntegrations,
}: InviteThirdPartyTeamsEligibilityProps): InviteThirdPartyTeamsState => {
	const analytics = useAnalytics(ExperimentsAnalytics);
	const [isLoading, setLoading] = useState(true);
	const [isEnabled, setEnabled] = useState(false);
	const [onSlackConnected, setOnSlackConnected] = useState(false);
	const { locale } = useIntl();
	const { inviteThirdPartyTeams } = useExperimentalFeatures();
	const { cloudId } = useInvitePeopleContext();

	const setOnSlackConnectedDelayed = () => {
		// Delay slack spotlight from appearing.
		setTimeout(() => setOnSlackConnected(true), 100);
	};

	const isSlackConnected = useMemo(
		() => integrations.has(Integration.PLATFORM_SLACK_SERVICE),
		[integrations],
	);

	const isSlackEnabled = useMemo(
		() => enabledIntegrations.has(Integration.PLATFORM_SLACK_SERVICE),
		[enabledIntegrations],
	);

	useEffect(() => {
		if (isThirdPartyStateLoading) {
			return;
		}
		if (!cloudId) {
			setLoading(false);
			setEnabled(false);
			return;
		}

		const ineligibilityReasons: InviteThirdPartyTeamsIneligibilityReasons[] = [];
		if (!isSlackEnabled) {
			ineligibilityReasons.push(InviteThirdPartyTeamsIneligibilityReasons.ORG_CONTROL_SETTINGS_OFF);
		}

		if (!isSlackConnected) {
			ineligibilityReasons.push(InviteThirdPartyTeamsIneligibilityReasons.SLACK_NOT_CONNECTED);
		}

		if (isMobile()) {
			ineligibilityReasons.push(InviteThirdPartyTeamsIneligibilityReasons.MOBILE);
		}
		setEnabled(ineligibilityReasons.length === 0);
		setLoading(false);
	}, [
		analytics,
		cloudId,
		isSlackConnected,
		locale,
		isThirdPartyStateLoading,
		inviteThirdPartyTeams,
		isSlackEnabled,
	]);

	return {
		isLoading,
		isEnabled,
		onSlackConnected,
		setOnSlackConnected: setOnSlackConnectedDelayed,
	};
};
